import React, { useCallback, useEffect, useState } from 'react';
import { IoArrowDown } from 'react-icons/io5';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Button, cn, Dropdown } from 'rizzui';
import SimpleBar from 'simplebar-react';
import WidgetCard from '../../../components/card/WidgetCard';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { DashboardService } from '../../../services/dashboard/dashboardService';
import { MODE_VIEW } from '../DashboardFilters';

interface ContractsInfoProps {
    className?: string
}

const ContractsInfo: React.FC<ContractsInfoProps> = ({ className }) => {

    const { filters } = useFiltersPR();
    const { filters: newFilters, updateFilters } = useFilters({ mode_view: 'daily', option: "mode-view-total-contracts" });
    const { handleErrors } = useHandleErrors();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState([]);

    const fetchOrders = useCallback(async (params: any) => {
        setIsLoading(true);
        try {
            const response = await new DashboardService().getModeViewContractsInfo(params);
            const responseData = response.getResponseData()?.data;
            if (responseData) {
                setData(responseData?.map((c: {
                    id: string;
                    quantity: string;
                    year: string;
                    month?: string;
                    week?: string;
                    day?: string;
                }) => {
                    let formattedDate = '';

                    if (c.day && c.month) {
                        formattedDate = `${c.day.padStart(2, '0')}-${c.month.padStart(2, '0')}-${c.year}`;
                    } else if (c.week && c.month) {
                        formattedDate = `Sem ${c.week}, ${c.month.padStart(2, '0')}-${c.year}`;
                    } else if (c.month) {
                        formattedDate = `${c.month.padStart(2, '0')}-${c.year}`;
                    } else {
                        formattedDate = c.year;
                    }

                    return {
                        Cantidad: parseInt(c.quantity, 10),
                        date: formattedDate,
                    };
                }));
            } else {
                handleErrors(response)
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [newFilters]);

    /**
     * Returns the title suffix based on the selected mode view.
     * @returns The title suffix string.
     */
    const getTitleSuffix = () => {
        const modeView = newFilters.filter_filters?.mode_view;
        switch (modeView) {
            case 'daily':
                return '(Vista diaria)';
            case 'weekly':
                return '(Vista semanal)';
            case 'monthly':
                return '(Vista mensual)';
            case 'yearly':
                return '(Vista anual)';
            default:
                return '';
        }
    };

    useEffect(() => {
        if (filters.filter_filters?.from && filters.filter_filters?.to) {
            fetchOrders({ filter_filters: { ...newFilters.filter_filters, ...filters.filter_filters } });
        }
    }, [fetchOrders, newFilters, filters]);

    return (
        <WidgetCard
            className={cn('@container', className)}
            title={`Número total de contratos ${getTitleSuffix()}`}
            action={<Dropdown>
                <Dropdown.Trigger>
                    <Button
                        as="span"
                        variant="outline"
                    >
                        {newFilters.filter_filters?.mode_view
                            ? MODE_VIEW.find((option) => option.value === newFilters.filter_filters?.mode_view)?.label
                            : 'Modo de vista'}
                        <IoArrowDown className="ml-2" />
                    </Button>
                </Dropdown.Trigger>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => { updateFilters({ mode_view: 'daily' }) }}>Diario</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ mode_view: 'weekly' }) }}>Semanal</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ mode_view: 'monthly' }) }}>Mensual</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updateFilters({ mode_view: 'yearly' }) }}>Anual</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>}
        >
            <SimpleBar>
                <div className="h-92 w-full @4xl:h-80 @7xl:h-88">
                    {data.length === 0 && !isLoading && (
                        <div className="flex items-center justify-center h-full text-gray-500">
                            No hay datos disponibles para el periodo seleccionado
                        </div>
                    )}
                    {data.length > 0 && (
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >
                            <AreaChart
                                data={data.length > 0 ? data : []}
                            /* margin={{
                                left: 50,
                                right: 15,
                                bottom: 25,
                            }} */
                            >
                                <defs>
                                    <linearGradient
                                        id="revenueGrowthGradient"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop offset="5%" stopColor="#FFCB2B1F" stopOpacity={1} />
                                        <stop
                                            offset="95%"
                                            stopColor="#FFCB2B05"
                                            stopOpacity={1}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="4 4"
                                    strokeWidth={1.5}
                                    horizontal={false}
                                />
                                <XAxis
                                    dataKey="date"
                                    axisLine={false}
                                    tickMargin={10}
                                    tickLine={false}
                                    angle={-45}
                                    textAnchor={'end'}
                                    height={newFilters.filter_filters?.mode_view === 'weekly' ? 120 : 90}
                                />
                                <Tooltip
                                    formatter={(value: number) => [`${value} contratos`, 'Cantidad']}
                                    labelFormatter={(label: string) => {
                                        const modeView = newFilters.filter_filters?.mode_view;
                                        switch (modeView) {
                                            case 'daily':
                                                return `Fecha: ${label}`;
                                            case 'weekly':
                                                return `${label}`;
                                            case 'monthly':
                                                return `Mes: ${label}`;
                                            case 'yearly':
                                                return `Año: ${label}`;
                                            default:
                                                return label;
                                        }
                                    }}
                                />
                                <Area
                                    strokeWidth={3}
                                    type="monotone"
                                    dataKey="Cantidad"
                                    fill="url(#revenueGrowthGradient)"
                                    activeDot={{ r: 8 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </SimpleBar>
        </WidgetCard>
    );
};

export default ContractsInfo;